import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Text } from '../../components/ui'
import type {
  SupportCenterHoursByWeekday,
  SupportCenterProfessionalRoleKind,
  SupportCenterProfileOption,
  SupportCenterSpecialization,
} from '../../types/supportCenterSpecialties'
import { SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS } from '../../types/supportCenterSpecialties'
import { WeekdayHourPickers } from './ParametrosFormFields'

type ParametrosAssignModalProps = {
  assignEditingId: string | null
  assignUserId: string
  assignRole: SupportCenterProfessionalRoleKind
  assignSpecializationIds: string[]
  assignWeekdays: number[]
  assignHoursByWeekday: SupportCenterHoursByWeekday
  assignError: string | null
  submitting: boolean
  profiles: SupportCenterProfileOption[]
  assignableSpecializations: SupportCenterSpecialization[]
  onUserChange: (userId: string) => void
  onRoleChange: (role: SupportCenterProfessionalRoleKind) => void
  onToggleSpecialization: (id: string) => void
  onWeekdaysChange: (next: number[]) => void
  onHoursByWeekdayChange: (next: SupportCenterHoursByWeekday) => void
  onClose: () => void
  onSave: () => void
}

const ParametrosAssignModal: React.FC<ParametrosAssignModalProps> = ({
  assignEditingId,
  assignUserId,
  assignRole,
  assignSpecializationIds,
  assignWeekdays,
  assignHoursByWeekday,
  assignError,
  submitting,
  profiles,
  assignableSpecializations,
  onUserChange,
  onRoleChange,
  onToggleSpecialization,
  onWeekdaysChange,
  onHoursByWeekdayChange,
  onClose,
  onSave,
}) => {
  const isAttendant = assignRole === 'atendente_triagem'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
            {assignEditingId ? 'Editar profissional' : 'Delegar profissional'}
          </Text>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Fechar
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profissional <span className="text-red-500">*</span>
            </label>
            <select
              value={assignUserId}
              disabled={!!assignEditingId}
              onChange={(e) => onUserChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
            >
              <option value="">Selecione...</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Função <span className="text-red-500">*</span>
            </label>
            <select
              value={assignRole}
              onChange={(e) => onRoleChange(e.target.value as SupportCenterProfessionalRoleKind)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="atendente_triagem">
                {SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS.atendente_triagem}
              </option>
              <option value="triagem">{SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS.triagem}</option>
            </select>
            <Text size="xs" color="muted" className="mt-1">
              {isAttendant
                ? 'Atende na agenda e pode ser responsável pela triagem.'
                : 'Somente responsável pela triagem — sem especialidade, dias ou horas.'}
            </Text>
          </div>
          {isAttendant && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Especialidades <span className="text-red-500">*</span>
                </label>
                <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 max-h-48 overflow-y-auto space-y-2">
                  {!assignUserId ? (
                    <Text size="sm" color="muted">
                      Selecione o profissional para listar as especialidades disponíveis.
                    </Text>
                  ) : assignableSpecializations.length === 0 ? (
                    <Text size="sm" color="muted">
                      {assignEditingId
                        ? 'Nenhuma especialidade cadastrada.'
                        : 'Este profissional já possui todas as especialidades cadastradas.'}
                    </Text>
                  ) : (
                    assignableSpecializations.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={assignSpecializationIds.includes(s.id)}
                          onChange={() => onToggleSpecialization(s.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        {s.name}
                      </label>
                    ))
                  )}
                </div>
                <Text size="xs" color="muted" className="mt-1">
                  Um profissional pode ter várias especialidades; elas aparecem juntas na lista.
                </Text>
              </div>
              <WeekdayHourPickers
                weekdays={assignWeekdays}
                hoursByWeekday={assignHoursByWeekday}
                onWeekdays={onWeekdaysChange}
                onHoursByWeekday={onHoursByWeekdayChange}
              />
            </>
          )}
        </div>
        {assignError && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{assignError}</Text>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" onClick={onSave} disabled={submitting}>
            {submitting ? 'Salvando...' : assignEditingId ? 'Salvar' : 'Delegar'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ParametrosAssignModal
